<template>
  <main
    class="creator-carousel"
    tabindex="0"
    @keydown.left.prevent="prev"
    @keydown.right.prevent="next"
    @touchstart="onTouchStart"
    @touchend="onTouchEnd"
  >
    <div class="background-circle circle-a" />
    <div class="background-circle circle-b" />

    <header class="brand-bar">
      <div class="brand">
        <img class="company-logo" :src="companyLogo" alt="吉享空间" />
        <span class="brand-divider" aria-hidden="true" />
        <span class="brand-logo" aria-hidden="true" />
        <span class="brand-title">创作者中心</span>
      </div>
    </header>

    <section
      :key="activeSlide.id"
      class="slide active"
    >
      <div class="slide-inner">
        <div class="slide-copy">
          <div class="eyebrow">{{ activeSlide.eyebrow }}</div>

          <h1 class="title">
            {{ activeSlide.title }}
            <br />
            <span>{{ activeSlide.highlight }}</span>
          </h1>

          <p class="description">
            {{ activeSlide.description }}
            <strong v-if="activeSlide.strongText">
              {{ activeSlide.strongText }}
            </strong>
          </p>

          <div class="tags">
            <span
              v-for="tag in activeSlide.tags"
              :key="tag"
              class="tag"
            >
              {{ tag }}
            </span>
          </div>

          <button
            v-if="activeSlide.showJoin"
            type="button"
            class="join-button"
            :disabled="joining"
            :aria-busy="joining ? 'true' : 'false'"
            @click="handleJoin"
          >
            加入我们
            <span aria-hidden="true">→</span>
          </button>
        </div>

        <div class="slide-visual">
          <div class="visual-backdrop" />
          <img
            :src="activeSlide.image"
            :alt="activeSlide.alt"
            draggable="false"
          />
        </div>
      </div>
    </section>

    <button
      v-if="!isFirst"
      type="button"
      class="nav-button nav-prev"
      aria-label="上一页"
      @click="prev"
    >
      <span aria-hidden="true">‹</span>
    </button>

    <button
      v-if="!isLast"
      type="button"
      class="nav-button nav-next"
      aria-label="下一页"
      @click="next"
    >
      <span aria-hidden="true">›</span>
    </button>
  </main>
</template>

<script>
import companyLogo from '@/assets/logo-header.png'
import serviceOrderImage from '../../assets/images/creatorCarousel/service-order.png'
import creativeMonetizationImage from '../../assets/images/creatorCarousel/creative-monetization.png'
import studentGrowthImage from '../../assets/images/creatorCarousel/8.png'

export default {
  name: 'CreatorCenterCarousel',

  props: {
    joining: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      companyLogo,
      current: 0,
      touchStartX: 0,
      slides: [
        {
          id: 'service-order',
          eyebrow: 'CREATOR SERVICE',
          title: '摄影师、化妆师',
          highlight: '在线派单、接单',
          description:
            '用户发布服务需求后，平台帮助撮合并派单，摄影师和化妆师可以直接接单合作。',
          strongText: '服务收入平台零抽佣。',
          tags: ['在线接单', '平台派单', '智能撮合', '零抽佣'],
          image: serviceOrderImage,
          alt: '摄影师与化妆师接单派单服务',
          showJoin: false
        },
        {
          id: 'creative-monetization',
          eyebrow: 'CREATE & EARN',
          title: '让原创作品',
          highlight: '实现创意变现',
          description:
            '将插画、风景图、可爱人偶等原创作品印制到衣服、背包、杯子等商品上，让作品产生实际价值。',
          strongText: '',
          tags: ['上传作品', '商品展示', '周边制作', '创作收益'],
          image: creativeMonetizationImage,
          alt: '原创作品制作成衣服背包和杯子',
          showJoin: false
        },
        {
          id: 'student-growth',
          eyebrow: 'FOR COLLEGE STUDENTS',
          title: '大学生的',
          highlight: '创作与成长舞台',
          description:
            '支持大学生通过摄影、化妆、绘画等技能获得兼职机会，尝试自主创业，把兴趣和专业能力转化为收入。',
          strongText: '',
          tags: ['灵活兼职', '创业尝试', '作品展示', '技能变现'],
          image: studentGrowthImage,
          alt: '摄影师化妆师和作画师向用户挥手',
          showJoin: true
        }
      ]
    }
  },

  computed: {
    activeSlide() {
      return this.slides[this.current]
    },

    isFirst() {
      return this.current === 0
    },

    isLast() {
      return this.current === this.slides.length - 1
    }
  },

  mounted() {
    this.$nextTick(() => {
      this.$el.focus()
    })
  },

  methods: {
    handleJoin() {
      if (this.joining) {
        return
      }
      this.$emit('join')
    },

    goTo(index) {
      const lastIndex = this.slides.length - 1
      const safeIndex = Math.max(0, Math.min(index, lastIndex))

      this.current = safeIndex
    },

    prev() {
      if (this.isFirst) {
        return
      }

      this.goTo(this.current - 1)
    },

    next() {
      if (this.isLast) {
        return
      }

      this.goTo(this.current + 1)
    },

    onTouchStart(event) {
      this.touchStartX = event.changedTouches[0].clientX
    },

    onTouchEnd(event) {
      const touchEndX = event.changedTouches[0].clientX
      const distance = touchEndX - this.touchStartX

      if (Math.abs(distance) < 50) {
        return
      }

      if (distance > 0) {
        this.prev()
      } else {
        this.next()
      }
    }
  }
}
</script>

<style>
.creator-carousel {
  --primary: #3158ee;
  --primary-soft: rgba(49, 88, 238, 0.08);
  --accent: #ffae24;
  --text: #171922;
  --muted: #6b7282;

  position: fixed;
  inset: 0;
  z-index: 999;
  min-width: 320px;
  width: 100vw;
  height: 100vh;
  height: 100dvh;
  min-height: 0;
  overflow: hidden;
  color: var(--text);
  background:
    radial-gradient(circle at 88% 14%, rgba(49, 88, 238, 0.07), transparent 27%),
    radial-gradient(circle at 10% 88%, rgba(255, 174, 36, 0.08), transparent 24%),
    #fff;
  font-family: "Microsoft YaHei", "PingFang SC", Arial, sans-serif;
  outline: none;
  isolation: isolate;
}

.creator-carousel *,
.creator-carousel *::before,
.creator-carousel *::after {
  box-sizing: border-box;
}

.creator-carousel .background-circle {
  position: absolute;
  z-index: 0;
  border-radius: 50%;
  pointer-events: none;
}

.creator-carousel .circle-a {
  width: 320px;
  height: 320px;
  right: -155px;
  top: -155px;
  background: rgba(49, 88, 238, 0.055);
}

.creator-carousel .circle-b {
  width: 210px;
  height: 210px;
  left: -105px;
  bottom: -105px;
  background: rgba(255, 174, 36, 0.075);
}

.creator-carousel .brand-bar {
  position: absolute;
  inset: 0 0 auto;
  z-index: 20;
  padding: 27px clamp(34px, 5vw, 84px);
}

.creator-carousel .brand {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  font-size: 16px;
  font-weight: 800;
  letter-spacing: 0.08em;
}

.creator-carousel .company-logo {
  display: block;
  width: 84px;
  height: 38px;
  object-fit: contain;
  object-position: left center;
}

.creator-carousel .brand-divider {
  width: 1px;
  height: 30px;
  background: #d9deed;
}

.creator-carousel .brand-title {
  white-space: nowrap;
}

.creator-carousel .brand-logo {
  position: relative;
  width: 34px;
  height: 34px;
  border-radius: 11px;
  background: linear-gradient(135deg, var(--primary), #6d87ff);
  box-shadow: 0 9px 24px rgba(49, 88, 238, 0.22);
  transform: rotate(-7deg);
}

.creator-carousel .brand-logo::after {
  content: "";
  position: absolute;
  width: 10px;
  height: 10px;
  right: -3px;
  top: -3px;
  border: 3px solid #fff;
  border-radius: 50%;
  background: var(--accent);
}

.creator-carousel .slide {
  position: absolute;
  inset: 0;
  z-index: 2;
}

.creator-carousel .slide-inner {
  width: 100%;
  height: 100%;
  display: grid;
  grid-template-columns: minmax(360px, 39%) minmax(0, 61%);
  align-items: center;
  gap: clamp(10px, 2vw, 44px);
  padding:
    clamp(96px, 12vh, 132px)
    clamp(80px, 7vw, 132px)
    clamp(68px, 8vh, 96px);
}

.creator-carousel .slide-copy {
  position: relative;
  z-index: 4;
  max-width: 570px;
}

.creator-carousel .eyebrow,
.creator-carousel .title,
.creator-carousel .description,
.creator-carousel .tags,
.creator-carousel .join-button {
  opacity: 0;
  transform: translateY(28px);
}

.creator-carousel .eyebrow {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 22px;
  color: var(--primary);
  font-size: clamp(12px, 0.95vw, 16px);
  font-weight: 800;
  letter-spacing: 0.17em;
}

.creator-carousel .eyebrow::before {
  content: "";
  width: 43px;
  height: 3px;
  border-radius: 999px;
  background: var(--accent);
}

.creator-carousel .title {
  margin: 0;
  font-size: clamp(46px, 4.7vw, 84px);
  line-height: 1.08;
  letter-spacing: -0.055em;
  font-weight: 900;
}

.creator-carousel .title span {
  color: var(--primary);
  font-size: inherit;
  font-weight: inherit;
  line-height: inherit;
  letter-spacing: inherit;
}

.creator-carousel .description {
  max-width: 540px;
  margin: 26px 0 0;
  color: var(--muted);
  font-size: clamp(17px, 1.2vw, 22px);
  line-height: 1.82;
}

.creator-carousel .description strong {
  color: #f49b17;
  font-size: inherit;
  font-weight: 800;
  line-height: inherit;
}

.creator-carousel .tags {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 27px;
}

.creator-carousel .tag {
  padding: 9px 15px;
  border: 1px solid rgba(49, 88, 238, 0.14);
  border-radius: 999px;
  color: #4157ab;
  background: rgba(49, 88, 238, 0.055);
  font-size: 14px;
  font-weight: 700;
}

.creator-carousel .join-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-width: 190px;
  height: 58px;
  margin-top: 34px;
  padding: 0 30px;
  border: 0;
  border-radius: 999px;
  color: #fff;
  background: #f49b17;
  box-shadow: 0 15px 36px rgba(244, 155, 23, 0.28);
  font-size: 18px;
  font-weight: 800;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.creator-carousel .join-button:hover {
  transform: translateY(-3px);
  box-shadow: 0 20px 44px rgba(244, 155, 23, 0.34);
}

.creator-carousel .join-button:disabled {
  cursor: not-allowed;
  opacity: 0.72;
  transform: none;
  box-shadow: none;
}

.creator-carousel .slide-visual {
  position: relative;
  z-index: 2;
  width: 100%;
  height: min(78vh, 800px);
  display: grid;
  place-items: center;
  opacity: 0;
  transform: translateY(26px) scale(0.95);
}

.creator-carousel .visual-backdrop {
  position: absolute;
  z-index: -1;
  width: 82%;
  height: 70%;
  right: 4%;
  top: 15%;
  border-radius: 49% 51% 43% 57% / 50% 43% 57% 50%;
  background:
    linear-gradient(
      145deg,
      rgba(49, 88, 238, 0.055),
      rgba(178, 190, 255, 0.13)
    );
  transform: rotate(-3deg);
}

.creator-carousel .slide-visual img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center;
  user-select: none;
  filter: drop-shadow(0 24px 34px rgba(34, 49, 112, 0.10));
}

.creator-carousel .active .slide-visual {
  animation: creator-carousel-visual-in 1.15s cubic-bezier(0.2, 0.75, 0.2, 1) both;
}

.creator-carousel .active .eyebrow {
  animation: creator-carousel-content-in 0.6s 0.92s ease both;
}

.creator-carousel .active .title {
  animation: creator-carousel-content-in 0.72s 1.22s cubic-bezier(0.18, 0.8, 0.2, 1) both;
}

.creator-carousel .active .description {
  animation: creator-carousel-content-in 0.68s 1.68s ease both;
}

.creator-carousel .active .tags {
  animation: creator-carousel-content-in 0.65s 1.98s ease both;
}

.creator-carousel .active .join-button {
  animation: creator-carousel-button-in 0.68s 2.24s cubic-bezier(0.18, 0.8, 0.2, 1) both;
}

.creator-carousel .nav-button {
  position: absolute;
  top: 50%;
  z-index: 30;
  width: 54px;
  height: 54px;
  padding: 0;
  border: 1px solid rgba(49, 88, 238, 0.12);
  border-radius: 50%;
  color: #33415f;
  background: rgba(255, 255, 255, 0.9);
  box-shadow: 0 12px 32px rgba(37, 51, 103, 0.12);
  backdrop-filter: blur(10px);
  cursor: pointer;
  transform: translateY(-50%);
  transition:
    color 0.2s ease,
    transform 0.2s ease,
    box-shadow 0.2s ease;
}

.creator-carousel .nav-button span {
  display: block;
  margin-top: -4px;
  font-size: 36px;
  line-height: 1;
}

.creator-carousel .nav-button:hover {
  color: var(--primary);
  transform: translateY(-50%) scale(1.06);
  box-shadow: 0 16px 38px rgba(49, 88, 238, 0.17);
}

.creator-carousel .nav-prev {
  left: clamp(20px, 2vw, 32px);
}

.creator-carousel .nav-next {
  right: clamp(20px, 2vw, 32px);
}

@keyframes creator-carousel-visual-in {
  from {
    opacity: 0;
    transform: translateY(26px) scale(0.95);
    filter: blur(4px);
  }

  to {
    opacity: 1;
    transform: translateY(0) scale(1);
    filter: blur(0);
  }
}

@keyframes creator-carousel-content-in {
  from {
    opacity: 0;
    transform: translateY(28px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes creator-carousel-button-in {
  from {
    opacity: 0;
    transform: translateY(22px) scale(0.95);
  }

  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@media (max-width: 960px) {
  .creator-carousel {
    min-height: 0;
  }

  .creator-carousel .brand-bar {
    padding: 18px 24px;
  }

  .creator-carousel .slide-inner {
    display: flex;
    flex-direction: column-reverse;
    justify-content: flex-end;
    gap: 0;
    padding: 72px 26px 44px;
  }

  .creator-carousel .slide-copy {
    width: 100%;
    max-width: none;
    padding: 0 28px;
  }

  .creator-carousel .slide-visual {
    width: 100%;
    height: min(48vh, 440px);
  }

  .creator-carousel .eyebrow {
    margin-bottom: 14px;
  }

  .creator-carousel .title {
    font-size: clamp(39px, 10vw, 58px);
  }

  .creator-carousel .description {
    margin-top: 16px;
    font-size: 16px;
    line-height: 1.66;
  }

  .creator-carousel .tags {
    margin-top: 17px;
  }

  .creator-carousel .join-button {
    height: 52px;
    margin-top: 21px;
  }

  .creator-carousel .nav-button {
    width: 44px;
    height: 44px;
  }

  .creator-carousel .nav-button span {
    font-size: 30px;
  }

  .creator-carousel .nav-prev {
    left: 20px;
  }

  .creator-carousel .nav-next {
    right: 20px;
  }
}

@media (max-width: 560px) {
  .creator-carousel {
    min-height: 0;
  }

  .creator-carousel .brand {
    font-size: 14px;
  }

  .creator-carousel .company-logo {
    width: 66px;
    height: 30px;
  }

  .creator-carousel .brand-divider {
    height: 24px;
  }

  .creator-carousel .brand-logo {
    width: 30px;
    height: 30px;
  }

  .creator-carousel .slide-inner {
    padding: 64px 18px 30px;
  }

  .creator-carousel .slide-copy {
    padding: 0 22px;
  }

  .creator-carousel .slide-visual {
    height: 40vh;
    min-height: 235px;
  }

  .creator-carousel .title {
    font-size: clamp(34px, 10.5vw, 46px);
  }

  .creator-carousel .description {
    font-size: 15px;
  }

  .creator-carousel .tag {
    padding: 7px 11px;
    font-size: 12px;
  }

  .creator-carousel .join-button {
    min-width: 165px;
    height: 48px;
    font-size: 16px;
  }

  .creator-carousel .nav-button {
    width: 40px;
    height: 40px;
  }

  .creator-carousel .nav-button span {
    font-size: 27px;
  }

  .creator-carousel .nav-prev {
    left: 20px;
  }

  .creator-carousel .nav-next {
    right: 20px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .creator-carousel *,
  .creator-carousel *::before,
  .creator-carousel *::after {
    animation-duration: 0.001ms !important;
    animation-iteration-count: 1 !important;
  }
}
</style>
